/**
 * Performance Logger Utility for GenFlappy
 *
 * Provides performance metrics logging for asset generation pipeline.
 * Story 4.5 - AC3: Performance Logging Implementation
 *
 * @module utils/performanceLogger
 */

import type { AssetType } from '../types/validation';

/**
 * Performance metrics for a single asset generation
 *
 * @interface PerformanceMetrics
 * @property {AssetType} assetType - Type of asset generated (character/obstacle/background)
 * @property {number} startTime - Generation start timestamp (ms from performance.now())
 * @property {number} endTime - Generation end timestamp (ms from performance.now())
 * @property {number} duration - Total generation time in milliseconds
 * @property {boolean} cacheHit - Whether asset was loaded from cache (true) or API (false)
 * @property {number} [apiLatency] - Replicate API call duration in milliseconds (undefined for cache hits)
 * @property {number} [rmbgDuration] - Background removal processing time in milliseconds
 * @property {number} [networkDuration] - Network transfer time in milliseconds
 * @property {number} [totalSize] - Asset size in bytes (base64 decoded size)
 *
 * @example
 * ```typescript
 * const metrics: PerformanceMetrics = {
 *   assetType: 'character',
 *   startTime: 1000,
 *   endTime: 9200,
 *   duration: 8200,
 *   cacheHit: false,
 *   apiLatency: 6100,
 *   rmbgDuration: 2100,
 * };
 * logGenerationPerformance(metrics);
 * // Output: [Performance] Character: 8.2s (API: 6.1s, rmbg: 2.1s, cache: miss)
 * ```
 */
export interface PerformanceMetrics {
  assetType: AssetType;
  startTime: number;
  endTime: number;
  duration: number;
  cacheHit: boolean;
  apiLatency?: number;
  rmbgDuration?: number;
  networkDuration?: number;
  totalSize?: number;
}

/**
 * Session-level aggregated performance metrics
 *
 * @interface SessionMetrics
 * @property {number} totalGenerationTime - Total time for all assets in milliseconds
 * @property {number} assetCount - Number of assets generated (0-3)
 * @property {number} cacheHits - Number of cache hits (0-3)
 * @property {number} averageGenerationTime - Average time per asset in milliseconds
 * @property {number} sessionStart - Session start timestamp
 *
 * @example
 * ```typescript
 * const session: SessionMetrics = {
 *   totalGenerationTime: 25000,
 *   assetCount: 3,
 *   cacheHits: 1,
 *   averageGenerationTime: 8333,
 *   sessionStart: Date.now(),
 * };
 * // Store in sessionStorage for persistence across page navigations
 * sessionStorage.setItem('genflappy_session_metrics', JSON.stringify(session));
 * ```
 */
export interface SessionMetrics {
  /** Total generation time in milliseconds */
  totalGenerationTime: number;

  /** Number of assets generated (0-3) */
  assetCount: number;

  /** Number of cache hits (0-3) */
  cacheHits: number;

  /** Average generation time per asset in milliseconds */
  averageGenerationTime: number;

  /** Session start timestamp */
  sessionStart: number;
}

/**
 * Formats milliseconds to seconds with 1 decimal place
 *
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "8.2s")
 *
 * @example
 * ```typescript
 * formatDuration(8234); // "8.2s"
 * formatDuration(1050); // "1.1s"
 * ```
 */
function formatDuration(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Logs asset generation performance metrics to console
 *
 * In development, logs detailed performance breakdown to console.
 * In production, this can be extended to send metrics to analytics service.
 *
 * Log format: `[Performance] AssetType: duration (API: api, rmbg: rmbg, cache: hit/miss)`
 *
 * @param {PerformanceMetrics} metrics - Performance metrics to log
 *
 * @example
 * ```typescript
 * logGenerationPerformance({
 *   assetType: 'character',
 *   startTime: 1000,
 *   endTime: 9200,
 *   duration: 8200,
 *   cacheHit: false,
 *   apiLatency: 6100,
 *   rmbgDuration: 2100,
 * });
 * // Output: [Performance] Character: 8.2s (API: 6.1s, rmbg: 2.1s, cache: miss)
 * ```
 *
 * @todo Story 8.2 - Send metrics to analytics service (Sentry/PostHog)
 */
export function logGenerationPerformance(metrics: PerformanceMetrics): void {
  const { assetType, duration, cacheHit, apiLatency, rmbgDuration } = metrics;

  // Format asset type for display (capitalize first letter)
  const assetTypeDisplay = assetType.charAt(0).toUpperCase() + assetType.slice(1);

  // Build log message with optional components
  const parts: string[] = [`[Performance] ${assetTypeDisplay}: ${formatDuration(duration)}`];

  // Add API latency if available (cache miss)
  if (apiLatency !== undefined) {
    parts.push(`API: ${formatDuration(apiLatency)}`);
  }

  // Add rmbg duration if available
  if (rmbgDuration !== undefined) {
    parts.push(`rmbg: ${formatDuration(rmbgDuration)}`);
  }

  // Add cache status
  parts.push(`cache: ${cacheHit ? 'hit' : 'miss'}`);

  // Combine parts: main message (duration), then details in parentheses
  const mainMessage = parts[0];
  const details = parts.slice(1).join(', ');
  const fullMessage = details ? `${mainMessage} (${details})` : mainMessage;

  // Log to console in development
  console.log(fullMessage);

  // TODO: Story 8.2 - Send metrics to analytics service
  // Example: analytics.track('asset_generation', metrics);
}
